import React, { useState, useEffect } from "react";
import {
  Heart,
  Droplet,
  Moon,
  Plus,
  Brain,
  Scale,
  GlassWater,
  Leaf,
  Smile,
  Activity,
  Star,
  ChevronRight,
  Thermometer,
  Zap,
  Calendar,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import useMetrics from "../hooks/useMetrics";
import { useAuth } from "../contexts/AuthContext";
import { useVitals } from "../contexts/VitalsContext";
import { deriveQureoScore, getQureoScoreLabel } from "../lib/qureoScore";
import { onHealthDataUpdated } from "../lib/healthSync";

/*  helpers  */
function moodToProgress(mood) {
  if (!mood) return null;
  const m = mood.toLowerCase();
  if (m.includes("great") || m.includes("excellent") || m.includes("happy")) return 85;
  if (m.includes("good") || m.includes("ok")) return 65;
  if (m.includes("fair") || m.includes("moderate")) return 50;
  if (m.includes("bad") || m.includes("sad") || m.includes("anxious")) return 30;
  if (m.includes("poor") || m.includes("terrible")) return 15;
  return 50;
}

function moodToStatus(mood) {
  if (!mood) return "";
  const m = mood.toLowerCase();
  if (m.includes("great") || m.includes("excellent") || m.includes("happy")) return "Great";
  if (m.includes("good") || m.includes("ok")) return "Good";
  if (m.includes("fair") || m.includes("moderate")) return "Fair";
  if (m.includes("bad") || m.includes("sad") || m.includes("anxious")) return "Bad";
  return mood;
}

function sleepToProgress(sleepLevel) {
  if (!sleepLevel) return null;
  const s = sleepLevel.toLowerCase();
  if (s.includes("excellent") || s === "great") return 90;
  if (s.includes("good")) return 72;
  if (s.includes("fair") || s.includes("ok") || s.includes("moderate")) return 52;
  if (s.includes("poor") || s.includes("bad") || s.includes("insomniac")) return 22;
  return 50;
}

function sleepToStatus(sleepLevel) {
  if (!sleepLevel) return "";
  const s = sleepLevel.toLowerCase();
  if (s.includes("excellent")) return "Excellent";
  if (s.includes("good")) return "Good";
  if (s.includes("fair") || s.includes("ok")) return "Fair";
  if (s.includes("poor") || s.includes("bad")) return "Poor";
  if (s.includes("insomniac")) return "Insomniac";
  return sleepLevel;
}

function statusHex(status) {
  if (!status || status === "") return "#94a3b8";
  const s = status.toLowerCase();
  if (["excellent", "normal", "active", "healthy", "optimal", "great"].includes(s)) return "#7841f4";
  if (["stable", "good"].includes(s)) return "#22c55e";
  if (["fair", "decent", "moderate", "upcoming"].includes(s)) return "#f59e0b";
  if (["bad", "poor", "insomniac", "needs work", "elevated", "high", "low"].includes(s)) return "#f43f5e";
  return "#64748b";
}

function statusBg(status) {
  return statusHex(status) + "20";
}

function statusRank(status) {
  if (!status) return 1;
  const s = status.toLowerCase();
  if (["excellent", "normal", "active", "healthy", "optimal", "great", "stable", "good"].includes(s)) return 3;
  if (["fair", "decent", "moderate", "upcoming", "none"].includes(s)) return 2;
  if (["bad", "poor", "insomniac", "needs work", "elevated", "high", "low"].includes(s)) return 1;
  return 2;
}

function rankToStatus(rank) {
  if (rank >= 2.6) return "Good";
  if (rank >= 1.8) return "Fair";
  return "Needs Work";
}

function ScoreRing({ score }) {
  const r = 54;
  const circ = 2 * Math.PI * r;
  const filled = ((score || 0) / 100) * circ;
  return (
    <svg width={128} height={128} viewBox="0 0 128 128">
      <circle cx={64} cy={64} r={r} fill="none" stroke="rgba(255,255,255,0.18)" strokeWidth={10} />
      <circle
        cx={64} cy={64} r={r}
        fill="none"
        stroke="#fff"
        strokeWidth={10}
        strokeDasharray={`${filled} ${circ}`}
        strokeLinecap="round"
        transform="rotate(-90 64 64)"
      />
    </svg>
  );
}

export default function HealthMetricsPage() {
  const navigate = useNavigate();
  const { metrics, fetchHealthSummary, getCachedHealthSummary } = useMetrics();
  const { assessments } = useAuth();
  const { vitals } = useVitals();
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    let mounted = true;

    const cached = getCachedHealthSummary();
    if (cached) {
      setSummary(cached);
    }

    fetchHealthSummary({ forceRefresh: true })
      .then((data) => {
        if (mounted) setSummary(data || null);
      })
      .catch(() => {
        if (!mounted) return;
        const fallback = getCachedHealthSummary();
        setSummary(fallback || null);
      });

    const refreshSummary = async () => {
      try {
        const data = await fetchHealthSummary({ forceRefresh: true });
        if (mounted) setSummary(data || null);
      } catch {
        if (!mounted) return;
        const fallback = getCachedHealthSummary();
        setSummary(fallback || null);
      }
    };

    const unsubscribeHealthSync = onHealthDataUpdated(refreshSummary, { debounceMs: 250 });

    return () => {
      mounted = false;
      unsubscribeHealthSync();
    };
  }, [fetchHealthSummary, getCachedHealthSummary]);

  const latestVitals = metrics?.vitals?.latest || {};
  const latestAssessment = Array.isArray(assessments) && assessments.length > 0
    ? [...assessments].sort((a, b) => new Date(b?.createdAt || 0) - new Date(a?.createdAt || 0))[0]
    : null;

  const appointmentCount = summary?.appointments?.upcoming ?? null;
  const weight = latestVitals?.weight ?? latestAssessment?.weightKg ?? latestAssessment?.weight ?? null;
  const bp = latestVitals?.bloodPressure ?? vitals?.bp ?? null;
  const heartRate = latestVitals?.heartRate ?? vitals?.heart ?? null;
  const temp = latestVitals?.temperature ?? vitals?.temp ?? null;
  const oxygen = latestVitals?.oxygenLevel ?? vitals?.oxygen ?? null;
  const qureoScore = deriveQureoScore({
    summaryScore: summary?.healthScore?.total,
    assessmentScore: latestAssessment?.score,
    bloodPressure: bp,
    heartRate,
    oxygenLevel: oxygen,
    temperature: temp,
  });
  const qureoLabel = summary?.healthScore?.label || getQureoScoreLabel(qureoScore);

  const hydration = vitals?.raw?.hydration ?? null;
  const sleepLevel = latestAssessment?.sleepLevel ?? null;
  const mood = latestAssessment?.mood ?? null;
  const fitnessLevel = typeof latestAssessment?.fitnessLevel === "number" ? latestAssessment.fitnessLevel : null;

  const mentalProgress = moodToProgress(mood);
  const activityProgress = fitnessLevel != null ? Math.min(100, Math.round((fitnessLevel / 10) * 100)) : null;
  const sleepProgress = sleepToProgress(sleepLevel);

  const metricsData = [
    {
      category: "Vitals",
      items: [
        {
          label: "Heart Rate",
          value: heartRate != null ? `${heartRate} bpm` : "",
          status: heartRate != null ? (heartRate < 60 ? "Low" : heartRate <= 100 ? "Normal" : "High") : "",
          icon: <Heart size={20} color="#f43f5e" fill="#f43f5e" />,
          bg: "#fff1f2",
        },
        {
          label: "Blood Pressure",
          value: bp ?? "",
          status: bp ? "Stable" : "",
          icon: <Droplet size={20} color="#8b5cf6" fill="#8b5cf6" />,
          bg: "#f5f3ff",
        },
        {
          label: "Oxygen Level",
          value: oxygen != null ? `${oxygen}%` : "",
          status: oxygen != null ? (oxygen >= 95 ? "Normal" : "Low") : "",
          icon: <Zap size={20} color="#0ea5e9" />,
          bg: "#f0f9ff",
        },
        {
          label: "Temperature",
          value: temp != null ? `${temp}F` : "",
          status: temp != null ? (temp < 99.5 ? "Normal" : "Elevated") : "",
          icon: <Thermometer size={20} color="#f97316" />,
          bg: "#fff7ed",
        },
      ],
    },
    {
      category: "Body",
      items: [
        {
          label: "Weight",
          value: weight != null ? `${Number(weight).toFixed(1)} kg` : "",
          status: weight != null ? "Stable" : "",
          icon: <Scale size={20} color="#3b82f6" />,
          bg: "#eff6ff",
        },
        {
          label: "Activity",
          value: fitnessLevel != null ? `Level ${fitnessLevel}/10` : "",
          status: fitnessLevel != null ? (fitnessLevel >= 7 ? "Active" : fitnessLevel >= 4 ? "Moderate" : "Low") : "",
          icon: <Activity size={20} color="#22c55e" />,
          bg: "#f0fdf4",
        },
      ],
    },
    {
      category: "Lifestyle",
      items: [
        {
          label: "Sleep",
          value: sleepLevel ?? "",
          status: sleepToStatus(sleepLevel),
          icon: <Moon size={20} color="#8b5cf6" />,
          bg: "#f5f3ff",
        },
        {
          label: "Hydration",
          value: hydration != null ? `${(hydration / 1000).toFixed(1)} L` : "",
          status: hydration != null ? (hydration >= 2000 ? "Optimal" : "Low") : "",
          icon: <GlassWater size={20} color="#0ea5e9" />,
          bg: "#f0f9ff",
        },
        {
          label: "Mood",
          value: mood ?? "",
          status: moodToStatus(mood),
          icon: <Smile size={20} color="#f59e0b" />,
          bg: "#fffbeb",
        },
        {
          label: "Appointments",
          value: appointmentCount != null ? `${appointmentCount} upcoming` : "",
          status: appointmentCount != null ? (appointmentCount > 0 ? "Upcoming" : "None") : "",
          icon: <Calendar size={20} color="#6c5ce7" />,
          bg: "#f5f3ff",
        },
      ],
    },
  ];

  const overviewIconByCategory = {
    Vitals: <Heart size={18} />,
    Body: <Scale size={18} />,
    Lifestyle: <Leaf size={18} />,
  };

  const overviewItems = [
    {
      icon: <Plus size={18} />,
      title: "Overall Health",
      status: qureoScore != null ? qureoLabel : "",
      progress: qureoScore ?? 0,
      note: qureoScore != null ? `Based on your Qureo Score of ${qureoScore}` : "Complete your health assessment",
    },
    ...metricsData.map((section) => {
      const total = section.items.length;
      const tracked = section.items.filter((item) => item.value).length;
      const progress = total > 0 ? Math.round((tracked / total) * 100) : 0;
      const avgRank = total > 0
        ? section.items.reduce((sum, item) => sum + statusRank(item.status), 0) / total
        : 0;
      const status = rankToStatus(avgRank);

      return {
        icon: overviewIconByCategory[section.category] || <Brain size={18} />,
        title: section.category,
        status,
        progress,
        note: `${tracked}/${total} metrics updated`,
      };
    }),
    {
      icon: <Brain size={18} />,
      title: "Mental Health",
      status: moodToStatus(mood),
      progress: mentalProgress ?? 0,
      note: mood ? `Mood reported as "${mood}"` : "No mood data available",
    },
    {
      icon: <Moon size={18} />,
      title: "Sleep Level",
      status: sleepToStatus(sleepLevel),
      progress: sleepProgress ?? 0,
      note: sleepLevel ? `Reported as "${sleepLevel}"` : "No sleep data available",
    },
    {
      icon: <Activity size={18} />,
      title: "Activity Level",
      status: fitnessLevel != null ? (fitnessLevel >= 7 ? "Active" : fitnessLevel >= 4 ? "Moderate" : "Low") : "",
      progress: activityProgress ?? 0,
      note: fitnessLevel != null ? `Fitness level ${fitnessLevel}/10` : "No activity data available",
    },
  ];

  const scoreComponents = summary?.healthScore?.components || {};
  const heroPills = [
    {
      label: "Physical",
      val: typeof scoreComponents.vitals === "number" ? `${scoreComponents.vitals}%` : (qureoScore != null ? `${qureoScore}%` : ""),
      color:
        (typeof scoreComponents.vitals === "number" ? scoreComponents.vitals : qureoScore) >= 70
          ? "#a7f3d0"
          : "#fca5a5",
    },
    {
      label: "Mental",
      val: typeof scoreComponents.lifestyle === "number" ? `${scoreComponents.lifestyle}%` : (mentalProgress != null ? `${mentalProgress}%` : ""),
      color:
        (typeof scoreComponents.lifestyle === "number" ? scoreComponents.lifestyle : mentalProgress) >= 60
          ? "#a7f3d0"
          : "#fca5a5",
    },
    {
      label: "Sleep",
      val: typeof scoreComponents.medicationAdherence === "number" ? `${scoreComponents.medicationAdherence}%` : (sleepProgress != null ? `${sleepProgress}%` : ""),
      color:
        (typeof scoreComponents.medicationAdherence === "number" ? scoreComponents.medicationAdherence : sleepProgress) >= 60
          ? "#a7f3d0"
          : "#fca5a5",
    },
  ];

  return (
    <div style={{ padding: 20, paddingBottom: 40 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
        <i
          onClick={() => navigate(-1)}
          className="fi fi-br-angle-left"
          style={{ color: "var(--primary)", fontSize: 20, cursor: "pointer" }}
        />
        <h3 style={{ fontWeight: 700, margin: 0, fontSize: 20, color: "#1f2937" }}>Health Metrics</h3>
      </div>
      <p style={{ fontSize: 13, color: "#777", marginLeft: 30, marginBottom: 20 }}>
        Overview of all your tracked health data
      </p>

      <div
        style={{
          background: "linear-gradient(135deg, var(--primary) 0%, #5b23c4 100%)",
          borderRadius: 28,
          padding: "20px 20px 22px",
          marginBottom: 24,
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            position: "absolute",
            width: 180,
            height: 180,
            borderRadius: "50%",
            background: "rgba(255,255,255,0.08)",
            top: -60,
            right: -50,
            pointerEvents: "none",
          }}
        />
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
          <div style={{ flex: 1 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
              <Star size={18} color="#fde68a" fill="#fde68a" />
              <span style={{ fontWeight: 700, fontSize: 15, color: "#fff", letterSpacing: 0.3 }}>Qureo Score</span>
            </div>
            <div style={{ fontSize: 13, color: "rgba(255,255,255,0.75)", marginBottom: 18, lineHeight: 1.4 }}>
              Reflects your overall health across lifestyle, vitals &amp; wellness.
            </div>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              {heroPills.map((s) => (
                <div key={s.label} style={{ background: "rgba(255,255,255,0.15)", borderRadius: 12, padding: "5px 12px" }}>
                  <div style={{ fontSize: 11, color: "rgba(255,255,255,0.7)" }}>{s.label}</div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: s.color }}>{s.val}</div>
                </div>
              ))}
            </div>
          </div>
          <div style={{ position: "relative", width: 128, height: 128, flexShrink: 0 }}>
            <ScoreRing score={qureoScore ?? 0} />
            <div
              style={{
                position: "absolute",
                inset: 0,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <span style={{ fontSize: 34, fontWeight: 800, color: "#fff", lineHeight: 1 }}>
                {qureoScore ?? ""}
              </span>
              <span style={{ fontSize: 12, color: "rgba(255,255,255,0.75)", marginTop: 2 }}>
                {qureoLabel}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div style={{ marginBottom: 24 }}>
        <h5 style={{ margin: "0 0 12px", fontWeight: 700, fontSize: 16, color: "#1f2937" }}>Overview</h5>
        <div style={{ background: "#f5f6f8", border: "1px solid #e2e6ec", borderRadius: 24, padding: 14 }}>
          {overviewItems.map((item, idx) => (
            <div
              key={item.title}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                padding: "11px 0",
                borderBottom: idx < overviewItems.length - 1 ? "1px solid #e7ebf1" : "none",
              }}
            >
              <div
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: 10,
                  display: "grid",
                  placeItems: "center",
                  background: "#fff",
                  color: "#334155",
                  border: "1px solid #e2e8f0",
                  flexShrink: 0,
                }}
              >
                {item.icon}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span style={{ fontWeight: 600, fontSize: 14, color: "#1f2937" }}>{item.title}</span>
                  <span style={{ fontWeight: 600, fontSize: 13, color: statusHex(item.status) }}>{item.status}</span>
                </div>
                <div style={{ marginTop: 7, height: 6, borderRadius: 999, background: "#dce3ec", overflow: "hidden" }}>
                  <div style={{ width: `${item.progress}%`, height: "100%", borderRadius: 999, background: statusHex(item.status) }} />
                </div>
                <div style={{ marginTop: 5, fontSize: 12, color: "#64748b" }}>{item.note}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {metricsData.map((section) => (
        <div key={section.category} style={{ marginBottom: 24 }}>
          <h5 style={{ margin: "0 0 10px", fontWeight: 700, fontSize: 15, color: "#1f2937" }}>{section.category}</h5>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {section.items.map((metric) => (
              <div
                key={metric.label}
                onClick={() =>
                  navigate(`/metrics/${metric.label.toLowerCase().replace(/ /g, "-")}`)
                }
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 14,
                  background: "#fff",
                  border: "1px solid #e8edf4",
                  borderRadius: 18,
                  padding: "13px 14px",
                  cursor: "pointer",
                }}
              >
                <div
                  style={{
                    width: 42,
                    height: 42,
                    borderRadius: 13,
                    background: metric.bg,
                    display: "grid",
                    placeItems: "center",
                    flexShrink: 0,
                  }}
                >
                  {metric.icon}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 14, color: "#1f2937" }}>{metric.label}</div>
                  <div style={{ fontSize: 13, color: "#64748b", marginTop: 2 }}>{metric.value}</div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
                  <span
                    style={{
                      fontSize: 12,
                      fontWeight: 600,
                      color: statusHex(metric.status),
                      background: statusBg(metric.status),
                      borderRadius: 8,
                      padding: "3px 9px",
                    }}
                  >
                       {metric.status}
                  </span>
                  <ChevronRight size={16} color="#94a3b8" />
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}